import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ShortContact } from '../../entities/short-contact.class';

@Component({
  selector: 'app-print-export-quote',
  templateUrl: './print-export-quote.component.html',
  styleUrls: ['./print-export-quote.component.scss'],
  standalone: false
})
export class PrintExportQuoteComponent implements OnInit {

  @ViewChild('chipInput') chipInputRef: ElementRef<HTMLInputElement>;

  form: UntypedFormGroup;
  selections: any = {};
  appStrings = this.appInfoService.appStrings;
  element: any;
  isOnline: boolean;
  isMobile: boolean;
  ensEmails: string[] = [];
  emailOptions: { value: string, label: string }[] = [];
  contactEmailOptions: { value: string, label: string }[] = [];
  emailChips: string[] = [];
  readonly separatorKeysCodes = [ENTER, COMMA, SPACE] as const;
  chipInputControl = new FormControl<string>('');
  filteredOptions: Observable<{ value: string, label: string }[]>;

  constructor(
    public dialogRef: MatDialogRef<PrintExportQuoteComponent>,
    private formBuilder: UntypedFormBuilder,
    private appInfoService: AppInfoService,
    private appStateService: AppStateService,
    private deviceService: DeviceDetectorService,
    @Inject(MAT_DIALOG_DATA) data: any) {
    this.element = data;
  }

  ngOnInit() {
    this.isOnline = this.appStateService.state.isOnline;
    this.isMobile = this.deviceService.isMobile();
    this.selections = {
      shouldPrintQuote: false,
      shouldExportQuote: false,
      shouldEmailQuote: false
    };

    const ensEmails: string[] = this.element.ensEmails || [];
    this.ensEmails = ensEmails;
    const contacts: ShortContact[] = this.element.contacts || [];
    const contactEmails = contacts.map(c => c.email).filter(e => !!e);
    const allEmails = [...new Set([...contactEmails, ...ensEmails])].filter(e => !!e);
    this.emailOptions = allEmails.map(e => ({ value: e, label: e }));
    this.contactEmailOptions = [...new Set(contactEmails)].map(e => ({ value: e, label: e }));

    this.filteredOptions = this.chipInputControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const typed = typeof value === 'string' ? value : '';
        const lower = typed.toLowerCase();
        return this.emailOptions.filter(opt =>
          !this.emailChips.includes(opt.value) &&
          (!lower || opt.label.toLowerCase().includes(lower))
        );
      })
    );

    this.form = this.formBuilder.group({
      emailAddresses: [[], [Validators.required]]
    });
  }

  get title(): string {
    if (!this.appStrings || !this.element) {
      return '';
    }

    if (this.element.isOrder) {
      return this.appStrings.order_emailOrderLabel;
    }

    return this.element.isEmailOnly ? this.appStrings.quote_emailQuoteLabel : this.appStrings.quote_printExportEmailLabelQuoteCreated;
  }

  get hasEnsEmails(): boolean {
    return this.ensEmails.length > 0;
  }

  get allRecipientsAdded(): boolean {
    return this.ensEmails.every((e: string) => this.emailChips.includes(e));
  }

  get hasContactEmails(): boolean {
    return this.contactEmailOptions.length > 0;
  }

  get isEmailButtonDisabled(): boolean {
    const pendingValue = (this.chipInputControl.value || '').trim();
    return !this.form.valid && !this.isValidEmail(pendingValue);
  }

  addChip(event: MatChipInputEvent) {
    const value = (event.value || '').trim();
    if (value && this.isValidEmail(value)) {
      this.addEmailsToChips([value]);
    }
    event.chipInput.clear();
    this.chipInputControl.setValue('');
  }

  selectFromAutocomplete(event: MatAutocompleteSelectedEvent) {
    const option = event.option.value as { value: string, label: string };
    this.addEmailsToChips([option.value]);
    if (this.chipInputRef) {
      this.chipInputRef.nativeElement.value = '';
    }
    this.chipInputControl.setValue('');
  }

  removeChip(email: string) {
    this.emailChips = this.emailChips.filter(e => e !== email);
    this.syncEmailControl();
    this.chipInputControl.setValue(this.chipInputControl.value ?? '');
  }

  addRecipients() {
    this.addEmailsToChips(this.ensEmails);
  }

  get allContactsAdded(): boolean {
    return this.contactEmailOptions.every(o => this.emailChips.includes(o.value));
  }

  addAllContacts() {
    this.addEmailsToChips(this.contactEmailOptions.map(o => o.value));
  }

  private addEmailsToChips(emails: string[]) {
    const toAdd = emails.filter(e => e && !this.emailChips.includes(e));
    if (!toAdd.length) {
      return;
    }
    this.emailChips = [...this.emailChips, ...toAdd];
    this.syncEmailControl();
    this.chipInputControl.setValue(this.chipInputControl.value ?? '');
  }

  private syncEmailControl() {
    this.form.controls.emailAddresses.setValue(this.emailChips);
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  close(): void {
    this.dialogRef.close(this.selections);
  }

  printQuote() {
    this.selections.shouldPrintQuote = true;
    this.close();
  }

  exportQuote() {
    this.selections.shouldExportQuote = true;
    this.close();
  }

  emailQuote() {
    const pendingValue = (this.chipInputControl.value || '').trim();
    if (pendingValue && this.isValidEmail(pendingValue)) {
      this.addEmailsToChips([pendingValue]);
      if (this.chipInputRef) {
        this.chipInputRef.nativeElement.value = '';
      }
      this.chipInputControl.setValue('');
    }
    this.selections.shouldEmailQuote = true;
    this.selections.emailAddresses = this.emailChips;
    this.close();
  }

}
